import React, { forwardRef, memo, useCallback, useState } from 'react';
import classNames from 'classnames';
import { maskitoDateOptionsGenerator } from '@maskito/kit';
import { DatePicker } from 'antd';
import { PickerRef } from '@rc-component/picker/es/interface';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import withFloatingLabel, {
  FloatingLabelProps,
} from '@lib/hocs/withFloatingLabel';
import Input from '@lib/components/Input/Input';
import { InputSizes } from '@lib/components/Input/enums';
import isMobile from '@lib/utils/isMobile';
import styles from './DateTimePicker.module.scss';

dayjs.extend(customParseFormat);

const DISPLAY_FORMAT = 'DD/MM';
const VALUE_FORMAT = 'MM-DD';

const monthDayMask = maskitoDateOptionsGenerator({
  mode: 'dd/mm',
  separator: '/',
});

function useSelectedMonthDay({
  value,
  isFocused,
}: {
  value?: string;
  isFocused?: boolean;
}) {
  const isValueExists = !!value;
  const currentYear = dayjs().year();
  const selected = isValueExists
    ? dayjs(`${currentYear}-${value}`, 'YYYY-MM-DD', true)
    : null;
  const validSelected = selected && selected.isValid() ? selected : null;
  const inputValue = validSelected ? validSelected.format(DISPLAY_FORMAT) : '';
  const placeholder = isFocused ? DISPLAY_FORMAT : undefined;
  return {
    selected: validSelected,
    inputValue,
    placeholder,
    isValueExists,
  };
}

type Props = {
  allowClear?: boolean;
  disabled?: boolean;
  hasError?: boolean;
  isFocused?: boolean;
  name?: string;
  onChange: (v: string) => void;
  size?: InputSizes;
  value?: string;
};

const MonthDayPickerCommon = forwardRef(
  (props: Props & FloatingLabelProps, ref: React.ForwardedRef<PickerRef>) => {
    const {
      allowClear: allowClearProp = true,
      disabled,
      hasError,
      isFocused,
      name,
      onChange,
      setIsFocused,
      size = InputSizes.large,
      value,
    } = props;
    const [isDropdownOpened, setDropdownOpened] = useState(false);
    const [rawInput, setRawInput] = useState<string | undefined>(undefined);
    const { phone, tablet } = isMobile();
    const isMobileDevice = phone || tablet;

    const { selected, inputValue, placeholder, isValueExists } =
      useSelectedMonthDay({ value, isFocused });

    const shouldUseOnBlur = !(isValueExists || isDropdownOpened);
    const onBlurHandler = shouldUseOnBlur
      ? (e: React.FocusEvent<HTMLElement>) => {
          const target = e?.relatedTarget as HTMLElement | null;
          const className =
            target?.className || (e?.target as HTMLElement)?.className || '';
          if (
            typeof className === 'string' &&
            className.indexOf('ant-picker') === -1
          ) {
            setIsFocused(false);
            setDropdownOpened(false);
          }
        }
      : undefined;

    const onCloseHandler = () => {
      setIsFocused(false);
      setDropdownOpened(false);
      setRawInput(undefined);
    };

    const onFocusHandler = () => {
      setDropdownOpened(true);
      setIsFocused(true);
      if (rawInput === undefined) setRawInput(inputValue);
    };

    const onChangeHandler = useCallback(
      (v: Dayjs | null) => {
        const newValue = v ? v.format(VALUE_FORMAT) : '';
        if (!v && isMobileDevice) {
          setIsFocused(false);
        }
        onChange(newValue);
        setTimeout(() => {
          setDropdownOpened(false);
        }, 0);
        setRawInput(undefined);
      },
      [isMobileDevice, onChange, setIsFocused],
    );

    const onInputChangeHandler = (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      const input = e.target.value;
      if (input === '') {
        setRawInput('');
        return;
      }
      setRawInput(input);
      const parsed = dayjs(input, DISPLAY_FORMAT, true);
      if (parsed.isValid()) {
        onChangeHandler(parsed);
      }
    };

    const onInputBlurHandler = () => {
      if (rawInput === '') {
        onChangeHandler(null);
        return;
      }
      if (rawInput !== undefined && rawInput !== '') {
        const parsed = dayjs(rawInput, DISPLAY_FORMAT, true);
        if (!parsed.isValid()) {
          setRawInput(undefined);
        }
      }
    };

    const onClearHandler = () => onChangeHandler(null);

    const className = classNames(styles.picker, {
      [styles.datePickerFilled]: isValueExists,
    });

    const allowClear = allowClearProp
      ? { clearIcon: <MaterialIcon icon="cancel" /> }
      : false;

    return (
      <div className={styles.rootDatePickerCommon}>
        <div className={styles.pickerWrapper}>
          <DatePicker
            allowClear={allowClear}
            className={className}
            defaultPickerValue={selected || dayjs()}
            disabled={disabled}
            format={DISPLAY_FORMAT}
            inputReadOnly={isMobileDevice}
            name={`${name}-picker`}
            needConfirm={false}
            onBlur={onBlurHandler}
            onChange={onChangeHandler}
            onFocus={onFocusHandler}
            open={isDropdownOpened}
            placeholder=""
            classNames={{
              popup: {
                root: styles.monthDayPickerPopup,
              },
            }}
            ref={ref}
            showNow={false}
            size="large"
            status={hasError ? 'error' : undefined}
            value={selected}
          />
        </div>
        <div className={styles.inputWrapper}>
          <Input
            disabled={disabled}
            disabledIcon="calendar_today"
            hasError={hasError}
            inputMode="numeric"
            mask={monthDayMask}
            name={name}
            onBlur={onInputBlurHandler}
            onChange={onInputChangeHandler}
            onClear={allowClearProp ? onClearHandler : undefined}
            onRightIconClick={onFocusHandler}
            placeholder={placeholder}
            rightIcon="calendar_today"
            rightIconSymbolsOutlined
            size={size}
            value={rawInput !== undefined ? rawInput : inputValue}
          />
        </div>
        {isDropdownOpened && (
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/control-has-associated-label,jsx-a11y/no-static-element-interactions
          <div className={styles.mask} onClick={onCloseHandler} />
        )}
      </div>
    );
  },
);

export default memo(withFloatingLabel(MonthDayPickerCommon));
